import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import BulkActionBar from '../../components/ui/BulkActionBar';
import ExportTable from './components/ExportTable';
import ExportStatsPanel from './components/ExportStatsPanel';
import ExportPreviewModal from './components/ExportPreviewModal';
import ExportFilters from './components/ExportFilters';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';

const ExportHistory = () => {
  const navigate = useNavigate();
  const { user, currentOrgId } = useAuth();
  const [selectedExports, setSelectedExports] = useState([]);
  const [filters, setFilters] = useState({
    search: '',
    dateRange: { from: '', to: '' },
    format: '',
    type: '',
    status: '',
    invoiceCount: '',
    fileSize: ''
  });
  const [isFiltersCollapsed, setIsFiltersCollapsed] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: 'exportDate', direction: 'desc' });
  const [previewModal, setPreviewModal] = useState({ isOpen: false, exportItem: null });

  // Live exports from Supabase (org scoped)
  const [exportsList, setExportsList] = useState([]);
  const [filteredExports, setFilteredExports] = useState([]);
  const [stats, setStats] = useState({
    totalExports: 0,
    totalSize: 0,
    formatDistribution: [],
    typeDistribution: [],
    recentActivity: { last7Days: 0, last30Days: 0, averagePerDay: 0 },
    storageUsage: { used: 0, limit: 0 }
  });

  // Load exports for current org
  useEffect(() => {
    const loadExports = async () => {
      if (!user?.id || !currentOrgId) return;
      try {
        const { data, error } = await supabase
          .from('exports')
          .select('id, created_at, format, count, file_path, user_id')
          .eq('org_id', currentOrgId)
          .order('created_at', { ascending: false });
        if (error) throw error;

        const mapped = (data || []).map((row) => {
          const createdAt = row.created_at || null;
          const ext = (row.format || '').toString().toLowerCase();
          const formatLabel = ext.toUpperCase();
          const filenameFromPath = row.file_path ? row.file_path.split('/').pop() : undefined;
          const filename = filenameFromPath || `export_${(createdAt || '').slice(0,10)}_${row.id}.${ext || 'csv'}`;
          // Try to infer type; default to Sage 50 as the primary workflow
          const type = 'Sage 50';
          return {
            id: row.id,
            filename,
            exportDate: createdAt,
            type,
            format: formatLabel || 'CSV',
            invoiceCount: row.count || 0,
            fileSize: 0,
            status: 'completed',
            expirationDate: null,
            downloadUrl: null, // optional: wire to storage if a bucket is defined for exports
            storagePath: row.file_path || null,
            createdBy: '',
            description: `Export of ${row.count || 0} invoices`
          };
        });

        setExportsList(mapped);
        setFilteredExports(mapped);

        // Compute lightweight stats
        const totalExports = mapped.length;
        const byFormat = mapped.reduce((acc, e) => { acc[e.format] = (acc[e.format] || 0) + 1; return acc; }, {});
        const formatDistribution = Object.entries(byFormat).map(([type, count]) => ({ type, count }));
        const byType = mapped.reduce((acc, e) => { acc[e.type] = (acc[e.type] || 0) + 1; return acc; }, {});
        const typeDistribution = Object.entries(byType).map(([name, count]) => ({ name, count }));
        const now = Date.now();
        const last7Days = mapped.filter(e => e.exportDate && (now - new Date(e.exportDate).getTime()) < 7*24*3600*1000).length;
        const last30Days = mapped.filter(e => e.exportDate && (now - new Date(e.exportDate).getTime()) < 30*24*3600*1000).length;
        const averagePerDay = Number((last30Days / 30).toFixed(2));

        setStats({
          totalExports,
          totalSize: 0,
          formatDistribution,
          typeDistribution,
          recentActivity: { last7Days, last30Days, averagePerDay },
          storageUsage: { used: 0, limit: 0 }
        });
      } catch (e) {
        console.error('Failed to load exports:', e);
        setExportsList([]);
        setFilteredExports([]);
      }
    };
    loadExports();
  }, [user?.id, currentOrgId]);

  // Filter and sort exports
  useEffect(() => {
    let filtered = [...exportsList];

    // Apply filters
    if (filters?.search) {
      const searchLower = filters?.search?.toLowerCase();
      filtered = filtered?.filter(exp => 
        exp?.filename?.toLowerCase()?.includes(searchLower) ||
        exp?.type?.toLowerCase()?.includes(searchLower) ||
        exp?.description?.toLowerCase()?.includes(searchLower)
      );
    }

    if (filters?.dateRange?.from) {
      filtered = filtered?.filter(exp => 
        new Date(exp.exportDate) >= new Date(filters.dateRange.from)
      );
    }

    if (filters?.dateRange?.to) {
      filtered = filtered?.filter(exp => 
        new Date(exp.exportDate) <= new Date(filters.dateRange.to + 'T23:59:59')
      );
    }

    if (filters?.format) {
      filtered = filtered?.filter(exp => exp?.format === filters?.format);
    }

    if (filters?.type) {
      filtered = filtered?.filter(exp => exp?.type === filters?.type);
    }

    if (filters?.status) {
      if (filters?.status === 'expired') {
        filtered = filtered?.filter(exp => new Date(exp.expirationDate) < new Date());
      } else {
        filtered = filtered?.filter(exp => exp?.status === filters?.status);
      }
    }

    if (filters?.invoiceCount) {
      filtered = filtered?.filter(exp => exp?.invoiceCount >= parseInt(filters?.invoiceCount));
    }

    if (filters?.fileSize) {
      filtered = filtered?.filter(exp => {
        const sizeMB = exp?.fileSize / (1024 * 1024);
        switch (filters?.fileSize) {
          case 'small': return sizeMB < 1;
          case 'medium': return sizeMB >= 1 && sizeMB <= 10;
          case 'large': return sizeMB > 10;
          default: return true;
        }
      });
    }

    // Apply sorting
    filtered?.sort((a, b) => {
      let aValue = a?.[sortConfig?.key];
      let bValue = b?.[sortConfig?.key];

      if (sortConfig?.key === 'exportDate' || sortConfig?.key === 'expirationDate') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }

      if (aValue < bValue) return sortConfig?.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig?.direction === 'asc' ? 1 : -1;
      return 0;
    });

    setFilteredExports(filtered);
  }, [filters, sortConfig, exportsList]);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev?.key === key && prev?.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleDownload = (exportItem) => {
    if (exportItem?.downloadUrl) {
      // Simulate download
      const link = document.createElement('a');
      link.href = exportItem?.downloadUrl;
      link.download = exportItem?.filename;
      document.body?.appendChild(link);
      link?.click();
      document.body?.removeChild(link);
    }
  };

  const handleRegenerate = (exportItem) => {
    // Simulate regeneration
    console.log('Regenerating export:', exportItem?.id);
  };

  const handlePreview = (exportItem) => {
    setPreviewModal({ isOpen: true, exportItem });
  };

  const handleBulkDownload = () => {
    const selectedItems = filteredExports?.filter(exp => 
      selectedExports?.includes(exp?.id) && exp?.status === 'completed' && exp?.downloadUrl
    );
    
    selectedItems?.forEach(item => {
      setTimeout(() => handleDownload(item), 100);
    });
    
    setSelectedExports([]);
  };

  const handleBulkRegenerate = () => {
    console.log('Regenerating exports:', selectedExports);
    setSelectedExports([]);
  };

  const handleBulkDelete = () => {
    console.log('Deleting exports:', selectedExports);
    setSelectedExports([]);
  };

  const bulkActions = [
    {
      label: 'Download Selected',
      icon: 'Download',
      onClick: handleBulkDownload,
      disabled: !selectedExports?.some(id => {
        const exp = filteredExports?.find(e => e?.id === id);
        return exp && exp?.status === 'completed' && exp?.downloadUrl;
      })
    },
    {
      label: 'Regenerate',
      icon: 'RefreshCw',
      onClick: handleBulkRegenerate,
      requiresConfirmation: true,
      confirmTitle: 'Regenerate Exports',
      confirmMessage: 'This will create new export files for the selected items. Existing files will remain available until they expire.'
    },
    {
      label: 'Delete',
      icon: 'Trash2',
      onClick: handleBulkDelete,
      destructive: true,
      requiresConfirmation: true,
      confirmTitle: 'Delete Exports',
      confirmMessage: 'This will permanently delete the selected export files. This action cannot be undone.'
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-6 py-8">
          {/* Page Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-foreground">Export History</h1>
              <p className="text-muted-foreground mt-2">
                Manage and download your exported files with comprehensive tracking
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <Button
                variant="outline"
                onClick={() => navigate('/invoices')}
                iconName="ArrowLeft"
                iconPosition="left"
              >
                Back to Invoices
              </Button>
              <Button
                variant="default"
                onClick={() => navigate('/invoices')}
                iconName="Plus"
                iconPosition="left"
              >
                New Export
              </Button>
            </div>
          </div>

          {/* Filters */}
          <ExportFilters
            filters={filters}
            onFiltersChange={setFilters}
            isCollapsed={isFiltersCollapsed}
            onToggleCollapse={() => setIsFiltersCollapsed(!isFiltersCollapsed)}
          />

          {/* Bulk Actions */}
          <BulkActionBar
            selectedCount={selectedExports?.length}
            totalCount={filteredExports?.length}
            actions={bulkActions}
            onSelectAll={() => setSelectedExports(filteredExports?.map(exp => exp?.id))}
            onClearSelection={() => setSelectedExports([])}
            position="sticky"
          />

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Main Content */}
            <div className="lg:col-span-3">
              <div className="bg-card border border-border rounded-lg p-6">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center space-x-3">
                    <Icon name="History" size={24} className="text-primary" />
                    <div>
                      <h2 className="text-xl font-semibold text-foreground">Export Files</h2>
                      <p className="text-sm text-muted-foreground">
                        {filteredExports?.length} of {exportsList?.length} exports
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => window.location?.reload()}
                      iconName="RefreshCw"
                      className="h-8 w-8 p-0"
                    />
                  </div>
                </div>

                <ExportTable
                  exports={filteredExports}
                  selectedExports={selectedExports}
                  onSelectionChange={setSelectedExports}
                  onDownload={handleDownload}
                  onRegenerate={handleRegenerate}
                  onPreview={handlePreview}
                  sortConfig={sortConfig}
                  onSort={handleSort}
                />
              </div>
            </div>

            {/* Stats Panel */}
            <div className="lg:col-span-1">
              <ExportStatsPanel stats={stats} />
            </div>
          </div>
        </div>
      </main>
      {/* Preview Modal */}
      <ExportPreviewModal
        exportItem={previewModal?.exportItem}
        isOpen={previewModal?.isOpen}
        onClose={() => setPreviewModal({ isOpen: false, exportItem: null })}
      />
    </div>
  );
};

export default ExportHistory;
